import { Button } from '@partful/shared/ui/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel } from '@partful/shared/ui/Dialog/Dialog';
import { Typography } from '@partful/shared/ui/Typography';
import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';

/**
 * Props for configuring a confirmation dialog
 */
type ConfirmationProps = {
  /** Optional title displayed at the top of the dialog */
  title?: string;
  /** Main message displayed in the dialog body */
  message: string;
  /** Text for the confirm button */
  confirmText: string;
  /** Text for the cancel button */
  cancelText: string;
  /** Callback function executed when confirm is clicked */
  onConfirm: () => Promise<void>;
};

export const ConfirmationContext = React.createContext<
  | {
      confirm: (props: ConfirmationProps) => void;
      close: () => void;
      disable: () => void;
      enable: () => void;
    }
  | undefined
>(undefined);

/**
 * Provider component for confirmation dialogs.
 * Manages the state and display of confirmation modals throughout the application.
 *
 * @param {PropsWithChildren} props - React children to be wrapped by the provider
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <ConfirmationProvider>
 *       <YourComponents />
 *     </ConfirmationProvider>
 *   );
 * }
 * ```
 *
 * @see useConfirmation - Hook to access confirmation dialog functionality
 */
export function ConfirmationProvider({ children }: PropsWithChildren) {
  const [confirmation, setConfirmation] = useState<ConfirmationProps | null>(null);
  const [disabled, setDisabled] = useState(false);

  const confirm = useCallback((props: ConfirmationProps) => {
    setConfirmation(props);
    setDisabled(false);
  }, []);

  const close = useCallback(() => {
    setConfirmation(null);
    setDisabled(false);
  }, []);

  const enable = useCallback(() => {
    setDisabled(false);
  }, []);

  const disable = useCallback(() => {
    setDisabled(true);
  }, []);

  const value = useMemo(() => ({ confirm, close, enable, disable }), [confirm, close, enable, disable]);

  return (
    <ConfirmationContext.Provider value={value}>
      {children}
      {confirmation && (
        <Dialog open={Boolean(confirmation)} modal onClose={close} data-testid="confirmation-dialog">
          <DialogPanel>
            <DialogHeader>
              {confirmation.title && <Typography appearance="heading5">{confirmation.title}</Typography>}
            </DialogHeader>
            <DialogContent>
              <Typography>{confirmation.message}</Typography>
            </DialogContent>
            <DialogFooter>
              <Button onClick={close} variant="tertiary" disabled={disabled} data-testid="confirmation-dialog-cancel">
                {confirmation.cancelText}
              </Button>
              <Button
                onClick={confirmation.onConfirm}
                variant="destructive"
                disabled={disabled}
                data-testid="confirmation-dialog-confirm"
              >
                {confirmation.confirmText}
              </Button>
            </DialogFooter>
          </DialogPanel>
        </Dialog>
      )}
    </ConfirmationContext.Provider>
  );
}
